/**
 * Videos Page
 *
 * Displays captured video clips from Alta Avigilon cameras.
 * Videos are captured automatically via 24/7 capture scheduler.
 * Features table layout with status indicators and filtering.
 */

import { useState, useMemo } from 'react';
import {
  Video,
  Search,
  AlertCircle,
  Loader2,
  Download,
  RefreshCw,
  Trash2,
  Clock,
  Calendar,
  Camera,
  ChevronLeft,
  ChevronRight,
  X,
  HardDrive,
  Film,
} from 'lucide-react';
import {
  useVideos,
  useRetryCapture,
  useCancelCapture,
  useDeleteVideo,
  type VideoClip,
  type VideoStatus,
} from '../../hooks/useVideos';
import { useCameraLookup } from '../../hooks/useCameras';
import { useCurrentUser } from '../../hooks/useUsers';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

// Status configuration
const STATUS_CONFIG: Record<VideoStatus, { label: string; color: string; bgColor: string }> = {
  scheduled: { label: 'Scheduled', color: 'text-gray-600', bgColor: 'bg-gray-100' },
  capturing: { label: 'Capturing', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  captured: { label: 'Captured', color: 'text-amber-600', bgColor: 'bg-amber-100' },
  processing: { label: 'Processing', color: 'text-purple-600', bgColor: 'bg-purple-100' },
  completed: { label: 'Completed', color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  failed: { label: 'Failed', color: 'text-red-600', bgColor: 'bg-red-100' },
};

export function VideosPage() {
  useDocumentTitle('Video Logs');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [cameraFilter, setCameraFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<VideoStatus | ''>('');
  const [page, setPage] = useState(1);

  // Data queries
  const { data: videosData, isLoading, error } = useVideos({
    page,
    limit: 20,
    cameraId: cameraFilter || undefined,
    status: statusFilter || undefined,
  });
  const { data: cameras } = useCameraLookup();

  // Mutations
  const retryMutation = useRetryCapture();
  const cancelMutation = useCancelCapture();
  const deleteMutation = useDeleteVideo();

  const videos = videosData?.data ?? [];
  const pagination = videosData?.pagination;
  const totalPages = pagination?.totalPages ?? 1;

  // Filter by search (client-side for camera name)
  const filteredVideos = useMemo(() => {
    if (!searchQuery) return videos;
    const query = searchQuery.toLowerCase();
    return videos.filter(
      (video) =>
        video.cameraName?.toLowerCase().includes(query) ||
        video.cameraId.toLowerCase().includes(query)
    );
  }, [videos, searchQuery]);

  // Handlers
  const handleRetry = async (id: string) => {
    await retryMutation.mutateAsync(id);
  };

  const handleCancel = async (id: string) => {
    if (window.confirm('Are you sure you want to cancel this capture?')) {
      await cancelMutation.mutateAsync(id);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this video clip?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-guild-mint/50">
          <Video size={24} className="text-guild-green" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            Video Logs
          </h1>
          <p className="mt-0.5 text-sm text-gray-500">
            View and manage captured video footage from cameras
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px]">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search by camera name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
              w-full rounded-xl border border-gray-200 bg-white
              pl-11 pr-4 py-2.5 text-sm
              placeholder:text-gray-400
              focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              transition-all duration-200
            "
          />
        </div>

        {/* Camera Filter */}
        <select
          value={cameraFilter}
          onChange={(e) => {
            setCameraFilter(e.target.value);
            setPage(1);
          }}
          className="
            rounded-xl border border-gray-200 bg-white
            px-4 py-2.5 text-sm
            focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
            transition-all duration-200
          "
        >
          <option value="">All Cameras</option>
          {cameras?.map((camera) => (
            <option key={camera.id} value={camera.cameraId}>
              {camera.name}
            </option>
          ))}
        </select>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as VideoStatus | '');
            setPage(1);
          }}
          className="
            rounded-xl border border-gray-200 bg-white
            px-4 py-2.5 text-sm
            focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
            transition-all duration-200
          "
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([value, config]) => (
            <option key={value} value={value}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {error ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Failed to load videos</p>
            <p className="text-sm text-red-600">
              Please try refreshing the page or contact support.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="text-guild-green animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading video clips...</p>
        </div>
      ) : filteredVideos.length > 0 ? (
        <>
          {/* Table */}
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Camera
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Lens
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Scheduled
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Duration
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Size
                    </th>
                    <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Resolution
                    </th>
                    <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredVideos.map((video) => (
                    <VideoRow
                      key={video.id}
                      video={video}
                      isAdmin={isAdmin}
                      onRetry={() => handleRetry(video.id)}
                      onCancel={() => handleCancel(video.id)}
                      onDelete={() => handleDelete(video.id)}
                      isRetrying={retryMutation.isPending}
                      isCancelling={cancelMutation.isPending}
                      isDeleting={deleteMutation.isPending}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages} ({pagination?.total ?? 0} total clips)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="
                    flex items-center gap-1 px-3 py-2 rounded-lg
                    text-sm font-medium text-gray-600
                    border border-gray-200 bg-white
                    hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors
                  "
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="
                    flex items-center gap-1 px-3 py-2 rounded-lg
                    text-sm font-medium text-gray-600
                    border border-gray-200 bg-white
                    hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed
                    transition-colors
                  "
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyState />
      )}
    </div>
  );
}

// Video Row Component
interface VideoRowProps {
  video: VideoClip;
  isAdmin: boolean;
  onRetry: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isRetrying: boolean;
  isCancelling: boolean;
  isDeleting: boolean;
}

function VideoRow({ video, isAdmin, onRetry, onCancel, onDelete, isRetrying, isCancelling, isDeleting }: VideoRowProps) {
  const statusConfig = STATUS_CONFIG[video.status];

  // Format date/time
  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const scheduled = formatDateTime(video.scheduledStartTime);

  // Format duration
  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format file size
  const formatSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const isProcessing = video.status === 'capturing' || video.status === 'processing';

  return (
    <tr className="group hover:bg-gray-50/50 transition-colors">
      {/* Camera */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 flex-shrink-0">
            <Camera size={16} className="text-gray-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {video.cameraName ?? 'Unknown Camera'}
            </p>
            <p className="text-xs text-gray-500 font-mono truncate">{video.cameraId}</p>
          </div>
        </div>
      </td>

      {/* Lens (Phase 34: multi-sensor capture visibility) */}
      <td className="px-5 py-4">
        <span className="text-sm text-gray-600">{video.sourceNumber ?? '—'}</span>
      </td>

      {/* Scheduled */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar size={14} className="text-gray-400" />
          <span>{scheduled.date}</span>
          <span className="text-gray-300">|</span>
          <Clock size={14} className="text-gray-400" />
          <span>{scheduled.time}</span>
        </div>
      </td>

      {/* Status */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-2">
          <span
            className={`
              inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold
              ${statusConfig.bgColor} ${statusConfig.color}
            `}
          >
            {isProcessing && (
              <Loader2 size={12} className="animate-spin" />
            )}
            {statusConfig.label}
          </span>
          {video.retryCount > 0 && (
            <span className="text-xs text-gray-400">
              (retry {video.retryCount})
            </span>
          )}
        </div>
        {video.errorMessage && (
          <p className="mt-1 text-xs text-red-600 max-w-xs truncate" title={video.errorMessage}>
            {video.errorMessage}
          </p>
        )}
      </td>

      {/* Duration */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <Film size={14} className="text-gray-400" />
          {formatDuration(video.durationSeconds)}
        </div>
      </td>

      {/* Size */}
      <td className="px-5 py-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <HardDrive size={14} className="text-gray-400" />
          {formatSize(video.fileSizeBytes)}
        </div>
      </td>

      {/* Resolution */}
      <td className="px-5 py-4">
        <span className="text-sm text-gray-600 font-mono">
          {video.resolution ?? '-'}
        </span>
      </td>

      {/* Actions */}
      <td className="px-5 py-4">
        <div className="flex items-center justify-end gap-2">
          {video.status === 'completed' && video.s3Key && (
            <a
              href={`/api/videos/${video.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                text-xs font-semibold text-guild-green
                bg-guild-mint/30 hover:bg-guild-mint/50
                transition-colors
              "
            >
              <Download size={14} />
              Download
            </a>
          )}

          {video.status === 'failed' && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                text-xs font-semibold text-amber-700
                bg-amber-100 hover:bg-amber-200
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              <RefreshCw size={14} className={isRetrying ? 'animate-spin' : ''} />
              Retry
            </button>
          )}

          {isAdmin && isProcessing && (
            <button
              onClick={onCancel}
              disabled={isCancelling}
              className="
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                text-xs font-semibold text-red-600
                bg-red-50 hover:bg-red-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              <X size={14} />
              Cancel
            </button>
          )}

          {isAdmin && !isProcessing && (
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className="
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                text-xs font-semibold text-red-600
                bg-red-50 hover:bg-red-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors opacity-0 group-hover:opacity-100
              "
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Empty State
function EmptyState() {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 mb-5">
          <Video size={32} className="text-gray-300" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">No video clips yet</h3>
        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          Video clips will appear here automatically as cameras capture footage continuously.
        </p>
      </div>
    </div>
  );
}

export default VideosPage;
